// ==========================================================================================
// NOMBRE DEL ARCHIVO: src/components/QuoteAction.jsx
// DESCRIPCIÓN: Maneja las acciones de Aprobar/Modificar cotización desde el PDF
// VERSIÓN: 1.1 - Compatible con App.js sin React Router
// ==========================================================================================

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
  TextField,
  Card,
  CardContent,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import EditIcon from '@mui/icons-material/Edit';

// API URL
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function QuoteAction() {
  // Detectar quoteNumber y action desde la URL (sin react-router)
  const pathMatch = window.location.pathname.match(/^\/quote\/([^/]+)\/(accept|modify)$/);
  const quoteNumber = pathMatch ? decodeURIComponent(pathMatch[1]) : null;
  const action = pathMatch ? pathMatch[2] : null;
  
  const [status, setStatus] = useState('loading'); // loading, confirm, processing, success, error
  const [quote, setQuote] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [modifyMessage, setModifyMessage] = useState('');
  const [modifyContact, setModifyContact] = useState('');
  
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (!quoteNumber) {
      setStatus('error');
      setErrorMsg('No se especificó número de cotización');
      return;
    }

    loadQuote();
  }, [quoteNumber]);

  // Cargar datos de la cotización
  const loadQuote = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/quotes/${quoteNumber}`);
      const data = await response.json();

      if (!data.ok || !data.quote) {
        throw new Error(data.error || 'Cotización no encontrada');
      }

      setQuote(data.quote);
      
      // Si ya está aprobada, mostrar mensaje
      if (data.quote.status === 'approved') {
        setStatus('already_approved');
      } else {
        setStatus('confirm');
      }

    } catch (err) {
      console.error('Error cargando cotización:', err);
      setStatus('error');
      setErrorMsg(err.message || 'Error al cargar la cotización');
    }
  };

  // Aprobar cotización
  const handleAccept = async () => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    setStatus('processing');

    try {
      const response = await fetch(`${API_BASE}/api/quotes/${quoteNumber}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'pdf_link' })
      });

      const data = await response.json();

      if (!data.ok) {
        throw new Error(data.message || data.error || 'Error al aprobar');
      }

      console.log('✅ Cotización aprobada:', data);
      setStatus('success');

    } catch (err) {
      console.error('Error aprobando:', err);
      setStatus('error');
      setErrorMsg(err.message || 'Error al aprobar la cotización');
      hasProcessed.current = false;
    }
  };

  // Solicitar modificaciones
  const handleModify = async () => {
    if (!modifyMessage.trim()) {
      alert('Por favor describe los cambios que necesitas');
      return;
    }

    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    setStatus('processing');

    try {
      const response = await fetch(`${API_BASE}/api/quotes/${quoteNumber}/modify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: modifyMessage,
          contact: modifyContact,
          source: 'pdf_link' 
        })
      });

      const data = await response.json();

      if (!data.ok) {
        throw new Error(data.message || data.error || 'Error al enviar solicitud');
      }

      console.log('✅ Solicitud de cambios enviada:', data);
      setStatus('modify_success');

    } catch (err) {
      console.error('Error enviando solicitud:', err);
      setStatus('error');
      setErrorMsg(err.message || 'Error al enviar la solicitud');
      hasProcessed.current = false;
    }
  };

  // Formatear moneda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  // --- RENDER ---
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
        p: 3
      }}
    >
      {/* LOADING */}
      {status === 'loading' && (
        <>
          <CircularProgress size={60} />
          <Typography sx={{ mt: 2 }}>Cargando cotización...</Typography>
        </>
      )}

      {/* PROCESSING */}
      {status === 'processing' && (
        <>
          <CircularProgress size={60} />
          <Typography sx={{ mt: 2 }}>
            {action === 'accept' ? 'Aprobando cotización...' : 'Enviando solicitud...'}
          </Typography>
        </>
      )}

      {/* CONFIRM ACCEPT */}
      {status === 'confirm' && action === 'accept' && quote && (
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ color: '#6366f1', fontWeight: 'bold' }}>
              Aprobar Cotización
            </Typography>
            
            <Box sx={{ my: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Cotización: <strong>{quote.quoteNumber}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cliente: <strong>{quote.client?.name || 'N/A'}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total: <strong>{formatCurrency(quote.total || quote.financials?.total)}</strong>
              </Typography>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              Al aprobar esta cotización, se generará la orden de servicio, 
              orden de bodega y se programará el evento en el calendario.
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleAccept}
                startIcon={<CheckCircleIcon />}
              >
                Confirmar Aprobación
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.location.href = '/'}
              >
                Cancelar
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* CONFIRM MODIFY */}
      {status === 'confirm' && action === 'modify' && quote && (
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ color: '#f97316', fontWeight: 'bold' }}>
              Solicitar Cambios
            </Typography>
            
            <Box sx={{ my: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Cotización: <strong>{quote.quoteNumber}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cliente: <strong>{quote.client?.name || 'N/A'}</strong>
              </Typography>
            </Box>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="¿Qué cambios necesitas?"
              placeholder="Describe los cambios que deseas realizar en la cotización..."
              value={modifyMessage}
              onChange={(e) => setModifyMessage(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Teléfono o email de contacto (opcional)"
              placeholder="Para que podamos contactarte"
              value={modifyContact}
              onChange={(e) => setModifyContact(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                sx={{ bgcolor: '#f97316', '&:hover': { bgcolor: '#ea580c' } }}
                size="large"
                onClick={handleModify}
                startIcon={<EditIcon />}
              >
                Enviar Solicitud
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.location.href = '/'}
              >
                Cancelar
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* SUCCESS - APPROVED */}
      {status === 'success' && (
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: '#22c55e', mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ color: '#22c55e', fontWeight: 'bold' }}>
              ¡Cotización Aprobada!
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              La cotización <strong>{quoteNumber}</strong> ha sido aprobada exitosamente.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Se ha generado la orden de servicio, orden de bodega y el evento 
              ha sido programado en el calendario.
            </Typography>
            <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
              <strong>Siguiente paso:</strong> Realiza el depósito del 50% de anticipo 
              para confirmar la reserva. Te contactaremos pronto con los detalles.
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* SUCCESS - MODIFY */}
      {status === 'modify_success' && (
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: '#f97316', mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ color: '#f97316', fontWeight: 'bold' }}>
              ¡Solicitud Enviada!
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Tu solicitud de cambios para la cotización <strong>{quoteNumber}</strong> ha sido recibida.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Nuestro equipo revisará tu solicitud y te contactará pronto 
              con una cotización actualizada.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* ALREADY APPROVED */}
      {status === 'already_approved' && (
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: '#6366f1', mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ color: '#6366f1', fontWeight: 'bold' }}>
              Cotización Ya Aprobada
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              La cotización <strong>{quoteNumber}</strong> ya fue aprobada anteriormente.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Si tienes alguna duda, contáctanos.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* ERROR */}
      {status === 'error' && (
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <ErrorIcon sx={{ fontSize: 80, color: '#ef4444', mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ color: '#ef4444', fontWeight: 'bold' }}>
              Error
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {errorMsg}
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
            >
              Reintentar
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
